import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLingui } from "@lingui/react";
import { format } from "date-fns";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { TimePicker } from "@/components/ui/time-picker";
import { Loader2 } from "lucide-react";
import { ActivityTypeSelector } from "./ActivityTypeSelector";
import { ActivityTypeFormFields } from "./ActivityTypeFormFields";
import { ChildSelector } from "@/components/capacity/ChildSelector";
import { useCreateActivity } from "@/hooks/useCreateActivity";
import { useUpdateActivity } from "@/hooks/useUpdateActivity";
import {
  getActivityFormSchema,
  getActivityCreateApiSchema,
  getActivityUpdateApiSchema,
  type ActivityFormData,
  type ActivityFormInput,
} from "@/schemas/activityPayloadSchemas";
import type { ActivityResponse } from "@/types/activities";
import { ActivityType } from "@/types/activities";

/**
 * Discriminated union for ActivityFormProps.
 * In create mode, activity is optional.
 * In edit mode, activity is required to ensure type safety.
 */
type ActivityFormProps = {
  onSuccess?: () => void;
  onCancel?: () => void;
} & (
  | { mode: "create"; activity?: ActivityResponse }
  | { mode: "edit"; activity: ActivityResponse }
);

/**
 * Activity Form Component
 *
 * Handles create and edit modes for activities
 * - Create: All fields editable, default values
 * - Edit: Child and type are read-only; date/time and data fields are editable
 */
export function ActivityForm({
  mode,
  activity,
  onSuccess,
  onCancel,
}: ActivityFormProps) {
  const { i18n } = useLingui();
  const createMutation = useCreateActivity();
  const updateMutation = useUpdateActivity();

  const schema = getActivityFormSchema(i18n);

  // Determine default values based on mode
  const defaultValues: Partial<ActivityFormInput> = mode === "create"
    ? {
        childId: "",
        type: ActivityType.ActivityTypeCheckIn,
        activityTime: new Date(),
        data: {},
        comments: [],
      }
    : {
        childId: activity?.child?.id || "",
        type: activity?.type || "",
        activityTime: activity?.activity_time ? new Date(activity.activity_time) : new Date(),
        data: activity?.data || {},
        comments: activity?.comments || [],
      };

  const form = useForm<ActivityFormInput, unknown, ActivityFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  // Watch the activity type to reset data when it changes in create mode
  const selectedType = form.watch("type");

  useEffect(() => {
    if (mode === "create") {
      // Reset data field when type changes to clear previous type-specific fields
      form.setValue("data", {});
    }
  }, [selectedType, mode, form]);

  const handleSubmit = async (formData: ActivityFormData) => {
    try {
      if (mode === "create") {
        const createApiSchema = getActivityCreateApiSchema();
        const apiData = createApiSchema.parse(formData);
        await createMutation.mutateAsync(apiData);
      } else if (mode === "edit") {
        // In edit mode, activity is guaranteed by the discriminated union
        const updateApiSchema = getActivityUpdateApiSchema();
        const apiData = updateApiSchema.parse({
          activityTime: formData.activityTime,
          data: formData.data,
          comments: formData.comments,
        });
        await updateMutation.mutateAsync({
          // `mode === "edit"` means `activity` is a persisted record, so `id` is set;
          // the generated ActivityResponse marks every field optional.
          id: activity.id!,
          data: apiData,
        });
      }
      onSuccess?.();
    } catch (error) {
      // Error handling is done in mutation hooks with toast notifications
      console.error("Activity form submission error:", error);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Child Selector - Searchable, Disabled in edit mode */}
        <FormField
          control={form.control}
          name="childId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {i18n._({ id: "activities.form.child", message: "Child" })} *{" "}
                <span className="text-muted-foreground font-normal text-xs">
                  ({i18n._({ id: "settlingIn.fields.childHint", message: "with current or upcoming contracts" })})
                </span>
              </FormLabel>
              <FormControl>
                {mode === "edit" ? (
                  // In edit mode, show read-only display of selected child
                  <div className="flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm opacity-50 cursor-not-allowed">
                    {activity?.child?.firstname} {activity?.child?.lastname}
                  </div>
                ) : (
                  <ChildSelector
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Activity Type Selector - Disabled in edit mode */}
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>{i18n._({ id: "activities.form.activityType", message: "Activity Type" })}</FormLabel>
              <FormControl>
                <ActivityTypeSelector
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={mode === "edit"}
                  placeholder={i18n._({ id: "activities.form.selectActivityType", message: "Select activity type" })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Activity Date and Time */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="activityTime"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{i18n._({ id: "activities.form.date", message: "Date" })}</FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value}
                    onChange={(date) => {
                      if (date) {
                        // Preserve time when date changes
                        const currentTime = field.value || new Date();
                        date.setHours(currentTime.getHours());
                        date.setMinutes(currentTime.getMinutes());
                        field.onChange(date);
                      } else {
                        field.onChange(undefined);
                      }
                    }}
                    placeholder={i18n._({ id: "activities.form.pickDate", message: "Pick a date" })}
                    showClearButton={false}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="activityTime"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{i18n._({ id: "activities.form.time", message: "Time" })}</FormLabel>
                <FormControl>
                  <TimePicker
                    value={field.value ? format(field.value, "HH:mm") : ""}
                    onChange={(time) => {
                      if (time) {
                        const [hours, minutes] = time.split(":");
                        const newDate = new Date(field.value || new Date());
                        newDate.setHours(parseInt(hours, 10));
                        newDate.setMinutes(parseInt(minutes, 10));
                        field.onChange(newDate);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Type-Specific Fields */}
        {selectedType && <ActivityTypeFormFields type={selectedType} form={form} />}

        {/* Comments */}
        <FormField
          control={form.control}
          name="comments.0.text"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{i18n._({ id: "activities.form.comments", message: "Comments (Optional)" })}</FormLabel>
              <FormControl>
                {/* The comment schema preprocesses its input, so the FORM value
                    is typed `unknown`; it holds a string in practice (the
                    default is `""` and the textarea only writes strings). */}
                <Textarea
                  {...field}
                  value={typeof field.value === "string" ? field.value : ""}
                  rows={3}
                  placeholder={i18n._({ id: "activities.form.commentsPlaceholder", message: "Add any additional notes..." })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Form Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            {i18n._({ id: "activities.form.cancel", message: "Cancel" })}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {mode === "create"
              ? i18n._({ id: "activities.form.create", message: "Create" })
              : i18n._({ id: "activities.form.saveChanges", message: "Save Changes" })}
          </Button>
        </div>
      </form>
    </Form>
  );
}
